// "Last edited by" cell — used in Refine list/show views over any audit-tracked
// table (every CC entity has updated_at / updated_by / agent_id).
//
// Display rules (from docs/audit/2026-04-30-audit-design.md):
//   - updated_at null         → render "—"
//   - updated_by null + agent → robot avatar; tooltip lists agent_id
//   - otherwise               → initials avatar + "{relative} ago"
//   - tooltip always shows: name (or "System"), absolute timestamp, "via X" if agent
//
// The avatar's own useUserMap call dedupes via React Query, so dropping this
// component into many list rows still costs one auth.users fetch.

import { formatDistanceToNow } from "date-fns";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/UserAvatar";
import { useUserMap } from "@/hooks/useUserMap";

interface LastEditedByProps {
  updated_at?: string | null;
  updated_by?: string | null;
  agent_id?: string | null;
}

export function LastEditedBy({
  updated_at,
  updated_by,
  agent_id,
}: LastEditedByProps) {
  const { userMap } = useUserMap();

  if (!updated_at) {
    return <span className="text-muted-foreground">—</span>;
  }

  const ts = new Date(updated_at);
  const relative = formatDistanceToNow(ts, { addSuffix: true });
  const absolute = ts.toLocaleString();

  const user = updated_by ? userMap[updated_by] : undefined;
  const name = updated_by
    ? (user?.display_name ?? user?.short_name ?? user?.email ?? "Unknown user")
    : "System";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-2">
            <UserAvatar userId={updated_by ?? null} agentId={agent_id} size="xs" />
            <span className="text-xs text-muted-foreground">{relative}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5 text-xs">
            <div className="font-medium text-popover-foreground">{name}</div>
            <div className="text-muted-foreground">{absolute}</div>
            {agent_id && (
              <div className="text-muted-foreground">via {agent_id}</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
